/**
 * Tenant Status Actions — lifecycle action buttons with confirmation dialogs.
 * Renders Verify/Suspend/Reactivate/Liquidate buttons based on current tenant status.
 * Liquidation is refused by the API (409) while the tenant ledger is non-zero.
 * @see Documentation/Specification/04_Processes/application-agnostic/tenant/TenantLifecycle.md
 */
import { useState } from 'react';
import { useUpdate, useNotify, useNavigate } from 'react-admin';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField, Chip, Alert,
} from '@mui/material';
import { Block, CheckCircle, DeleteForever, Verified } from '@mui/icons-material';

interface Props {
  tenantId: string;
  status: string;
}

const STATUS_ACTIONS: Record<string, { action: string; label: string; color: 'warning' | 'success' | 'error'; icon: React.ReactNode; confirmColor: 'warning' | 'success' | 'error' }[]> = {
  PendingVerification: [
    { action: 'verify', label: 'Verify', color: 'success', icon: <Verified />, confirmColor: 'success' },
  ],
  Active: [
    { action: 'suspend', label: 'Suspend', color: 'warning', icon: <Block />, confirmColor: 'warning' },
    { action: 'liquidate', label: 'Liquidate', color: 'error', icon: <DeleteForever />, confirmColor: 'error' },
  ],
  Suspended: [
    { action: 'reactivate', label: 'Reactivate', color: 'success', icon: <CheckCircle />, confirmColor: 'success' },
    { action: 'liquidate', label: 'Liquidate', color: 'error', icon: <DeleteForever />, confirmColor: 'error' },
  ],
  Liquidated: [],
};

const CONFIRM_MESSAGES: Record<string, { title: string; description: string; requireReason: boolean; confirmColor: 'warning' | 'success' | 'error' }> = {
  verify: {
    title: 'Verify Tenant',
    description: 'This activates a tenant that is pending verification. Users will be able to sign in.',
    requireReason: false,
    confirmColor: 'success',
  },
  suspend: {
    title: 'Suspend Tenant',
    description: 'This will immediately block all users from this tenant. Existing sessions will be invalidated and entitlements frozen. The tenant can be reactivated later.',
    requireReason: false,
    confirmColor: 'warning',
  },
  reactivate: {
    title: 'Reactivate Tenant',
    description: 'This will restore access for all users of this tenant and unfreeze frozen entitlements.',
    requireReason: false,
    confirmColor: 'success',
  },
  liquidate: {
    title: 'Liquidate Tenant',
    description: 'This action is IRREVERSIBLE. Entitlements are revoked, sessions invalidated and all tenant users deactivated. The API refuses liquidation while the tenant ledger balance is non-zero.',
    requireReason: true,
    confirmColor: 'error',
  },
};

export const TenantStatusActions = ({ tenantId, status }: Props) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [update] = useUpdate();
  const [dialog, setDialog] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const actions = STATUS_ACTIONS[status] ?? [];

  if (actions.length === 0) return null;

  const confirmInfo = dialog ? CONFIRM_MESSAGES[dialog] : null;

  const handleConfirm = async () => {
    if (!dialog) return;
    setLoading(true);
    try {
      await update('tenants', {
        id: tenantId,
        meta: { method: 'POST', path: `${tenantId}/${dialog}` },
        data: { reason: reason || undefined },
      });
      notify(`Tenant ${dialog}ed successfully`, { type: 'success' });
      setDialog(null);
      setReason('');
      navigate('/tenants');
    } catch (err: any) {
      // Surface API error body (409 NONZERO_BALANCE / INVALID_TRANSITION etc.)
      const apiMessage = err?.body?.message ?? err?.body?.error?.message ?? err?.message;
      notify(apiMessage ?? `Failed to ${dialog} tenant`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {actions.map((a) => (
          <Button
            key={a.action}
            variant="outlined"
            color={a.color}
            startIcon={a.icon}
            onClick={() => setDialog(a.action)}
            size="small"
          >
            {a.label}
          </Button>
        ))}
      </Box>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmInfo?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{confirmInfo?.description}</DialogContentText>
          {confirmInfo?.requireReason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This action is permanent and cannot be undone.
            </Alert>
          )}
          {confirmInfo?.requireReason && (
            <TextField
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(null); setReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmInfo?.confirmColor}
            onClick={handleConfirm}
            disabled={loading || (confirmInfo?.requireReason && !reason)}
          >
            {loading ? 'Processing...' : `Confirm ${confirmInfo?.title}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
